import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { Button, Input, InputOptionBox, InputType } from '../../../../core/components/Index';
import { Appl } from '../../../../core/Appl';
import { useState } from 'react';
import Helper from '../../../../core/services/Helper';

const SubmitApplication = observer(() => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.SubmitApplication, memberId!);
    vm.submitApplicationValidationMessage = "";
    vm.submitApplicationValidationMessageTitile = "Attention: Submission Not Yet Available";
    let toDate: (string | Date) | null | undefined = vm.model.personalInformation.retirementEffDate;
    if (!toDate) {
      if (vm.submission?.retirementDate) {
        toDate = vm.submission.retirementDate;
      }
    }
    let dateCheck = await vm.doRetirementDateValidationAsync(toDate as Date, true);
    if (dateCheck !== "") {
      vm.submitApplicationValidationMessageTitile = `Attention: Retirement Date No Longer Valid`;
      vm.submitApplicationValidationMessage = dateCheck;
    }
  }, []);

  return (
    <>
      {
        vm.submitApplicationValidationMessage !== "" &&
        <>
          <div className="gap-4 p-6 border-[2px] border-[#D82042] bg-[#d8204212] flex rounded-md mb-4" >
            <div><svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
              <path d="M12.5 25.5C15.8152 25.5 18.9946 24.183 21.3388 21.8388C23.683 19.4946 25 16.3152 25 13C25 9.68479 23.683 6.50537 21.3388 4.16117C18.9946 1.81696 15.8152 0.5 12.5 0.5C9.18479 0.5 6.00537 1.81696 3.66117 4.16117C1.31696 6.50537 0 9.68479 0 13C0 16.3152 1.31696 19.4946 3.66117 21.8388C6.00537 24.183 9.18479 25.5 12.5 25.5ZM12.5 6.75C13.1494 6.75 13.6719 7.27246 13.6719 7.92188V13.3906C13.6719 14.04 13.1494 14.5625 12.5 14.5625C11.8506 14.5625 11.3281 14.04 11.3281 13.3906V7.92188C11.3281 7.27246 11.8506 6.75 12.5 6.75ZM10.9375 17.6875C10.9375 17.2731 11.1021 16.8757 11.3951 16.5826C11.6882 16.2896 12.0856 16.125 12.5 16.125C12.9144 16.125 13.3118 16.2896 13.6049 16.5826C13.8979 16.8757 14.0625 17.2731 14.0625 17.6875C14.0625 18.1019 13.8979 18.4993 13.6049 18.7924C13.3118 19.0854 12.9144 19.25 12.5 19.25C12.0856 19.25 11.6882 19.0854 11.3951 18.7924C11.1021 18.4993 10.9375 18.1019 10.9375 17.6875Z" fill="#D82042" />
            </svg>
            </div>
            <div className='-mt-1'>
              <div className='text-[#D82042] text-22px font-semibold mb-1 tracking--1percent'>{vm.submitApplicationValidationMessageTitile}</div>
              <p className="text-black text-15px tracking--1_5percent  ">
                {Helper.formattedHtml(vm.submitApplicationValidationMessage!)}
              </p>
            </div>
          </div>
        </>
      }

      <div className="space-y-[36px] relative">
        {isSubmitting && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center select-none pointer-events-auto">
            <div className="bg-white px-10 py-12 rounded-lg shadow-lg text-center max-w-lg">
              <div className="animate-spin h-12 w-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Submitting Application</h3>
              <p className="text-gray-600">
                Please wait while we process your application and prepare it for electronic signature through DocuSign.
              </p>
            </div>
          </div>
        )}
        <div className="space-y-[15px]">
          <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Submit Your Retirement Application</h1>
          <p className="text-black text-15px tracking--1percent leading-26px ">
            You're almost there! This is your final chance to {' '}
            <span className="text-primary-500 underline font-semibold" style={{ cursor: 'pointer' }} onClick={() => vm.navigateToStepAsync(`${Steps.ReviewApplication}`)}>review your retirement application</span> {' '} before submitting it. Please take a moment to carefully review all information to ensure accuracy and completeness.  </p>
        </div>
        <div className="space-y-[15px] text-black text-15px leading-26px tracking--1percent ">
          <h2 className="font-semibold text-black text-22px">Electronic Signature Process</h2>
          <p>
            To complete your application, you will electronically sign and initial required sections via DocuSign. This will include:
          </p>
          <p className="text-black text-15px">
            <ol className='list-decimal ml-4'>
              <li>Option Election: You have chosen your preferred retirement benefit option (Unmodified, Option 1, 2, 3, or 4) and understand how this choice will affect your monthly benefit and any potential survivor benefits.</li>
              <li>Tax Withholding: You have confirmed your Federal and California State tax withholding preferences, understanding how these choices will impact the taxes applied to your monthly retirement benefit.</li>
              <li>Authorization for Direct Deposit: You have provided the necessary bank account information and authorize SCERS to deposit your monthly retirement payments directly into the account(s) specified.</li>
              <li>Service Retirement Acknowledgment: You affirm that all information provided in your application is true and accurate, and you agree to the terms and conditions of your SCERS retirement plan.</li>
              <li>Temporary Annuity (if applicable): If you have elected the Temporary Annuity, you acknowledge that you understand both the temporary increase and the permanent reduction to your SCERS pension starting at age 62.</li>
            </ol>
          </p>
          <p>
            The name displayed below is the legal name associated with your SCERS account and will be used for your electronic signature. By clicking "Submit Application," you acknowledge that your electronic signature and initials will be legally binding, just as if you had signed with pen and paper.
          </p>
        </div>
        <p className='mt-4'>
          <div>
            <Input
              id="fullName"
              value={vm.model.personalInformation.fullName}
              inputType={InputType.Text}
              label="Your Full Name"
              rows={200}
              onChange={(e) => {
                vm.onInputChange(e, 'fullName', vm.model.personalInformation);
              }}
              required={false}
              readOnly={true}
            />
          </div>
          <div className='mt-4 flex items-start'>
            <InputOptionBox
              inputId="submitDeclaration"
              value={vm.model?.personalInformation?.submitDeclaration}
              checked={vm.model?.personalInformation?.submitDeclaration!}
              disabled={vm.submitApplicationValidationMessage !== ""}
              onChange={(e: any) => { vm.model.personalInformation.submitDeclaration = !vm.model?.personalInformation.submitDeclaration! }}
            />
            <label htmlFor="submitDeclaration" className="ml-1 text-black text-15px tracking--1percent leading-26px cursor-pointer"         >
              I understand that by proceeding, I will be redirected to DocuSign to electronically sign my retirement application.
            </label>
          </div>
        </p>
        <div className="flex space-x-4 mt-6">
          <Button label='Submit Application' rounded
            onClick={async () => {
              setIsSubmitting(true);
              try {
                await vm.sendApplicationForSignAsync();
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={!vm.model.personalInformation.submitDeclaration}
          />
        </div>
      </div>
    </>
  );
});
export default SubmitApplication;



